// Settings → Business: what the business sells (up to three kinds) and what it offers
// (goods, and services as well). Board KindsSettings, docs/plans/business-kinds.md.
// The parts are in the business form: they change its state and save with its Save.
import { Badge, Button, Modal, Switch } from '@ims/ui'
import { Info, X } from '@ims/ui/icons'
import { useState } from 'react'
import { BUSINESS_TYPES, type BusinessType } from '../lib/catalog.ts'
import { KindPicker, kindsLabel } from './kind-picker.tsx'
import { SettingsPart } from './settings-part.tsx'

const labelOf = (k: BusinessType) => BUSINESS_TYPES.find((t) => t.id === k)?.label ?? k

export function KindsParts({
  kinds,
  savedKinds,
  onKinds,
  services,
  onServices,
  ownFields,
  onUseTheirFields,
  canEdit,
}: {
  kinds: BusinessType[]
  /** The kinds as saved: the question about fields is asked only once they differ. */
  savedKinds: BusinessType[]
  onKinds: (kinds: BusinessType[]) => void
  services: boolean
  onServices: (on: boolean) => void
  /** The business chose its own product fields (Settings → Product fields). */
  ownFields: boolean
  onUseTheirFields: () => void
  canEdit: boolean
}) {
  const [picking, setPicking] = useState<BusinessType[] | null>(null)
  const [keptMine, setKeptMine] = useState(false)
  const changed = kinds.join() !== savedKinds.join()
  const restaurant = kinds[0] === 'restaurant'
  const shown: BusinessType[] = kinds.length ? kinds : ['general']
  return (
    <>
      <SettingsPart
        title="What you sell"
        about="Up to three kinds. Their product fields come on together; the main kind gives the examples in empty boxes."
      >
        <div className="sm:col-span-2">
          <ul className="flex flex-wrap items-center gap-2" aria-label="Your kinds">
            {shown.map((k, i) => (
              <li
                key={k}
                className="inline-flex h-8 items-center gap-1.5 rounded-full border-[1.5px] border-primary bg-primary-light pl-3 pr-1.5 text-sm font-semibold"
              >
                {labelOf(k)}
                {i === 0 && (
                  <Badge tone="primary" className="font-semibold">
                    Main
                  </Badge>
                )}
                {canEdit && shown.length > 1 && (
                  <button
                    type="button"
                    aria-label={`Take ${labelOf(k)} off`}
                    className="grid size-6 place-items-center rounded-full text-muted hover:bg-soft hover:text-ink"
                    onClick={() => onKinds(kinds.filter((x) => x !== k))}
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
          {canEdit && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setPicking(kinds)}>
                Change kinds
              </Button>
              {kinds[1] && (
                <Button
                  variant="ghost"
                  onClick={() => onKinds([kinds[1]!, ...kinds.filter((k) => k !== kinds[1])])}
                >
                  Make {labelOf(kinds[1])} the main kind
                </Button>
              )}
            </div>
          )}
          {changed && ownFields && !keptMine && (
            <div
              role="status"
              className="mt-4 flex items-start gap-3 rounded-xl bg-info-light p-3 text-sm"
            >
              <Info className="mt-0.5 size-4 shrink-0 text-info" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">
                  Use the product fields of {kinds.length > 1 ? 'these kinds' : 'this kind'}?
                </p>
                <p className="text-muted">
                  You chose your own fields earlier. Your products and bills stay as they are; this
                  only changes which boxes the product form shows.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" onClick={onUseTheirFields}>
                    Use their fields
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setKeptMine(true)}>
                    Keep mine
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </SettingsPart>
      {!restaurant && (
        <SettingsPart title="What you offer" about="Goods, services, or both.">
          <Switch
            label="We sell goods"
            description="Products you keep in stock, receive and sell. Always on for now: businesses that only provide services come later."
            checked
            disabled
            readOnly
          />
          <Switch
            label="We also provide services"
            description="Repairs, fitting, treatments, soil tests. Products gets “Add service”: sold by the job, never counted in stock."
            checked={services}
            disabled={!canEdit}
            onChange={(e) => onServices(e.target.checked)}
          />
        </SettingsPart>
      )}
      <Modal
        open={picking !== null}
        onClose={() => setPicking(null)}
        title="What do you sell?"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPicking(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (picking) onKinds(picking)
                setPicking(null)
              }}
            >
              Use {picking && picking.length ? kindsLabel(picking) : 'Other shop'}
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-muted">
          Pick up to three. The first is your main kind. Products and bills stay as they are.
        </p>
        <div data-own-save>{picking && <KindPicker kinds={picking} onChange={setPicking} />}</div>
      </Modal>
    </>
  )
}
